import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Grid,
  Avatar,
  InputAdornment,
  Paper,
  Stack,
  Tooltip,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";

import {
  Add,
  Search,
  EditOutlined,
  DeleteOutline,
  Badge,
  PhotoCamera,
  ContactPage,
  Close,
  LocalShipping,
} from "@mui/icons-material";

import {
  collection,
  getDocs,
  query,
  where,
  doc,
  setDoc,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";

import {
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

import {
  createUserWithEmailAndPassword,
  getAuth,
} from "firebase/auth";

import { db, storage } from "../../firebase";
import Swal from "sweetalert2";

const PRIMARY = "#0f766e";
const PAGE_SIZE = 10;

function Drivermgmt() {
  const auth = getAuth();

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImg, setPreviewImg] = useState("");

  const [uploading, setUploading] = useState("");

  const [schoolId, setSchoolId] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    mobile: "",
    address: "",
    batchBilla: "",
    aadharNo: "",
    licenseNo: "",
    selfPhoto: "",
    licensePhoto: "",
    status: "Active",
  });

  // ================= FETCH SCHOOL =================

  useEffect(() => {
    fetchSchool();
  }, []);

  const fetchSchool = async () => {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) return;

      const q = query(
        collection(db, "users"),
        where("uid", "==", currentUser.uid)
      );

      const snap = await getDocs(q);

      if (!snap.empty) {
        setSchoolId(snap.docs[0].data().schoolId);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // ================= LOAD DRIVERS =================

  useEffect(() => {
    if (schoolId) {
      fetchDrivers();
    }
  }, [schoolId]);

  const fetchDrivers = async () => {
    setLoading(true);

    try {
      const q = query(
        collection(db, "users"),
        where("role", "==", "Driver"),
        where("schoolId", "==", schoolId)
      );

      const snap = await getDocs(q);

      setDrivers(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  // ================= FILTER =================

  const filteredDrivers = useMemo(() => {
    return drivers.filter(
      (d) =>
        d.name?.toLowerCase().includes(search.toLowerCase()) ||
        d.mobile?.includes(search)
    );
  }, [drivers, search]);

  const paginatedDrivers = filteredDrivers.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // ================= HANDLE CHANGE =================

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // ================= FILE UPLOAD =================

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];

    if (!file) return;

    setUploading(type);

    try {
      const storageRef = ref(
        storage,
        `drivers/${Date.now()}_${file.name}`
      );

      await uploadBytes(storageRef, file);

      const url = await getDownloadURL(storageRef);

      if (type === "self") {
        setForm((prev) => ({
          ...prev,
          selfPhoto: url,
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          licensePhoto: url,
        }));
      }
    } catch (err) {
      Swal.fire("Error", "Upload failed", "error");
    }

    setUploading("");
  };

  // ================= SAVE DRIVER =================

  const saveDriver = async () => {
    if (
      !form.name ||
      !form.mobile ||
      !form.licenseNo
    ) {
      return Swal.fire(
        "Required",
        "Name, Mobile & License No required",
        "warning"
      );
    }

    setLoading(true);

    try {
      let uid = editingId;

      // NEW DRIVER

      if (!editingId) {
        const userCredential =
          await createUserWithEmailAndPassword(
            auth,
            form.email,
            form.password
          );

        uid = userCredential.user.uid;
      }

      await setDoc(doc(db, "users", uid), {
        uid,
        schoolId,
        role: "Driver",

        name: form.name,
        email: form.email,
        mobile: form.mobile,
        address: form.address,

        batchBilla: form.batchBilla,
        aadharNo: form.aadharNo,
        licenseNo: form.licenseNo,

        selfPhoto: form.selfPhoto,
        licensePhoto: form.licensePhoto,

        status: form.status,

        createdAt: editingId
          ? form.createdAt
          : Timestamp.now(),

        updatedAt: Timestamp.now(),
      });

      Swal.fire(
        "Success",
        editingId
          ? "Driver Updated"
          : "Driver Added",
        "success"
      );

      resetForm();

      fetchDrivers();

      setOpen(false);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }

    setLoading(false);
  };

  // ================= DELETE =================

  const deleteDriver = async (uid) => {
    const conf = await Swal.fire({
      title: "Delete Driver?",
      text: "Driver account will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
    });

    if (conf.isConfirmed) {
      await deleteDoc(doc(db, "users", uid));

      fetchDrivers();

      Swal.fire(
        "Deleted",
        "Driver removed successfully",
        "success"
      );
    }
  };

  // ================= RESET =================

  const resetForm = () => {
    setEditingId(null);

    setForm({
      name: "",
      email: "",
      password: "",
      mobile: "",
      address: "",
      batchBilla: "",
      aadharNo: "",
      licenseNo: "",
      selfPhoto: "",
      licensePhoto: "",
      status: "Active",
    });
  };

  // ================= UI =================

  return (
    <Box bgcolor="#f1f5f9" minHeight="100vh" p={2}>
      <Grid className="row" spacing={2}>
        {/* ================= FORM ================= */}

        <Grid className="col-sm-4 mb-2" item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 4,
              position: "sticky",
              top: 15,
            }}
          >
            <Box
              p={2}
              borderBottom="1px solid #e2e8f0"
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <LocalShipping color="primary" />

                <Typography
                  fontWeight={800}
                  variant="h6"
                >
                  {editingId
                    ? "Edit Driver"
                    : "Add Driver"}
                </Typography>
              </Stack>
            </Box>

            <Box p={2}>
              <Stack spacing={2}>
                <TextField
                  size="small"
                  label="Driver Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  fullWidth
                />

                <TextField
                  size="small"
                  label="Email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                />

                {!editingId && (
                  <TextField
                    size="small"
                    label="Password"
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    fullWidth
                  />
                )}

                <TextField
                  size="small"
                  label="Mobile"
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  fullWidth
                />

                <TextField
                  size="small"
                  label="Address"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  fullWidth
                />

                <TextField
                  size="small"
                  label="Batch Billa"
                  name="batchBilla"
                  value={form.batchBilla}
                  onChange={handleChange}
                  fullWidth
                />

                <TextField
                  size="small"
                  label="Aadhar No"
                  name="aadharNo"
                  value={form.aadharNo}
                  onChange={handleChange}
                  fullWidth
                />

                <TextField
                  size="small"
                  label="License No"
                  name="licenseNo"
                  value={form.licenseNo}
                  onChange={handleChange}
                  fullWidth
                />

                {/* PHOTO */}

                <Box textAlign="center">
                  <Typography
                    variant="caption"
                    fontWeight={700}
                  >
                    DRIVER PHOTO
                  </Typography>

                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {uploading === "self"
                      ? "Uploading..."
                      : "Upload Photo"}

                    <input
                      hidden
                      type="file"
                      onChange={(e) =>
                        handleFileUpload(e, "self")
                      }
                    />
                  </Button>

                  {form.selfPhoto && (
                    <Avatar
                      src={form.selfPhoto}
                      sx={{
                        width: 70,
                        height: 70,
                        mx: "auto",
                        mt: 2,
                      }}
                    />
                  )}
                </Box>

                {/* LICENSE */}

                <Box textAlign="center">
                  <Typography
                    variant="caption"
                    fontWeight={700}
                  >
                    LICENSE PHOTO
                  </Typography>

                  <Button
                    component="label"
                    variant="outlined"
                    color="secondary"
                    startIcon={<ContactPage />}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    {uploading === "license"
                      ? "Uploading..."
                      : "Upload License"}

                    <input
                      hidden
                      type="file"
                      onChange={(e) =>
                        handleFileUpload(
                          e,
                          "license"
                        )
                      }
                    />
                  </Button>

                  {form.licensePhoto && (
                    <Avatar
                      src={form.licensePhoto}
                      variant="rounded"
                      sx={{
                        width: 90,
                        height: 60,
                        mx: "auto",
                        mt: 2,
                        borderRadius: 2,
                      }}
                    />
                  )}
                </Box>

                {/* SAVE */}

                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    bgcolor: PRIMARY,
                    py: 1.3,
                    borderRadius: 2,
                    fontWeight: 700,
                  }}
                  onClick={saveDriver}
                >
                  {editingId
                    ? "Update Driver"
                    : "Save Driver"}
                </Button>
              </Stack>
            </Box>
          </Card>
        </Grid>

        {/* ================= TABLE ================= */}

        <Grid className="col-sm-8 mb-2" item xs={12} md={8}>
          <Paper
            sx={{
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            {/* TOP */}

            <Box
              p={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Badge color="primary" />

                <Typography
                  variant="h6"
                  fontWeight={800}
                >
                  Drivers List
                </Typography>

                <Chip
                  label={`${filteredDrivers.length} Drivers`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Stack>

              <TextField
                size="small"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                sx={{ width: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* TABLE */}

            <Table size="small">
              <TableHead
                sx={{ bgcolor: "#f8fafc" }}
              >
                <TableRow>
                  <TableCell>
                    <b>DRIVER</b>
                  </TableCell>

                  <TableCell>
                    <b>MOBILE</b>
                  </TableCell>

                  <TableCell>
                    <b>BATCH</b>
                  </TableCell>

                  <TableCell>
                    <b>LICENSE</b>
                  </TableCell>

                  <TableCell>
                    <b>PHOTO</b>
                  </TableCell>

                  <TableCell align="right">
                    <b>ACTIONS</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                    >
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedDrivers.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Avatar
                            src={row.selfPhoto}
                          />

                          <Box>
                            <Typography
                              fontWeight={700}
                              fontSize={14}
                            >
                              {row.name}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {row.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        {row.mobile}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            row.batchBilla ||
                            "N/A"
                          }
                        />
                      </TableCell>

                      <TableCell>
                        {row.licensePhoto ? (
                          <Tooltip title="View License">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setPreviewImg(
                                  row.licensePhoto
                                );

                                setPreviewOpen(
                                  true
                                );
                              }}
                            >
                              <Avatar
                                src={
                                  row.licensePhoto
                                }
                                variant="rounded"
                                sx={{
                                  width: 45,
                                  height: 45,
                                  borderRadius: 2,
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography
                            variant="caption"
                            color="error"
                          >
                            No License
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="caption"
                          display="block"
                        >
                          ADR :
                          {row.aadharNo}
                        </Typography>

                        <Typography
                          variant="caption"
                          fontWeight={700}
                        >
                          LIC :
                          {row.licenseNo}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setEditingId(
                                row.id
                              );

                              setForm({
                                name:
                                  row.name || "",
                                email:
                                  row.email || "",
                                mobile:
                                  row.mobile || "",
                                address:
                                  row.address ||
                                  "",
                                batchBilla:
                                  row.batchBilla ||
                                  "",
                                aadharNo:
                                  row.aadharNo ||
                                  "",
                                licenseNo:
                                  row.licenseNo ||
                                  "",
                                selfPhoto:
                                  row.selfPhoto ||
                                  "",
                                licensePhoto:
                                  row.licensePhoto ||
                                  "",
                                status:
                                  row.status ||
                                  "Active",
                              });

                              setOpen(true);
                            }}
                          >
                            <EditOutlined />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() =>
                              deleteDriver(
                                row.id
                              )
                            }
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* EMPTY */}

            {filteredDrivers.length === 0 && (
              <Box p={4} textAlign="center">
                <Typography color="text.secondary">
                  No Drivers Found
                </Typography>
              </Box>
            )}

            {/* PAGINATION */}

            <Box
              p={2}
              display="flex"
              justifyContent="center"
            >
              <Pagination
                count={Math.ceil(
                  filteredDrivers.length /
                    PAGE_SIZE
                )}
                page={page}
                onChange={(e, v) => setPage(v)}
                color="primary"
                size="small"
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ================= LICENSE PREVIEW ================= */}

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          License Preview

          <IconButton
            onClick={() =>
              setPreviewOpen(false)
            }
            sx={{
              position: "absolute",
              right: 10,
              top: 10,
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent>
          <img
            src={previewImg}
            alt="License"
            style={{
              width: "100%",
              borderRadius: 12,
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Drivermgmt;